import { useQuery } from "@tanstack/react-query";
import { getDashboardSummary } from "@/api/endpoints";
import type { DashboardSummary } from "@/api/types";

const STATUS_LABELS: Record<string, string> = {
  new: "受注済み",
  allocated: "引き当て済み",
  label_ready: "ラベル取得済み",
  printed: "印刷済み",
  shipped: "発送完了",
  error: "エラー",
};

const PRINT_STATUS_LABELS: Record<string, string> = {
  pending: "待機中",
  printing: "印刷中",
  completed: "完了",
  failed: "失敗",
};

const FULFILL_STATUS_LABELS: Record<string, string> = {
  success: "成功",
  failed: "失敗",
  retrying: "再試行中",
};

function StatCard({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="stat-card">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-3xl font-bold text-brand-primary mt-1">{value}</span>
      {hint && <span className="text-xs text-gray-400 mt-1">{hint}</span>}
    </div>
  );
}

export default function DashboardPage() {
  const { data, isLoading, error, refetch, isFetching } = useQuery<DashboardSummary>({
    queryKey: ["dashboard"],
    queryFn: getDashboardSummary,
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return <p className="text-gray-500">読み込み中…</p>;
  }
  if (error || !data) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">
        ダッシュボードの取得に失敗しました: {String(error)}
      </div>
    );
  }

  const total = data.orders.total;
  const byStatus = data.orders.by_status;
  const byPlatform = data.orders.by_platform;

  // 未処理 NEW の合計件数 (ラベル発行漏れ検知用、2026-06-09 応急措置)。
  // 一括ラベル発行後にここが 0 でないなら漏れの可能性。
  // 過去メモ: 2026-06-09 に Shopee 34件が一括発行から漏れ、Wonect 側に痕跡が
  // 残らないままプラットフォーム側で手動処理する事故が発生したため警告を追加。
  const newCount = byStatus["new"] ?? 0;
  // platform×status から NEW の店舗別内訳を取得
  const newByPlatform = Object.entries(byPlatform)
    .map(([p, counts]) => [p, counts["new"] ?? 0] as const)
    .filter(([, c]) => c > 0)
    .sort(([, a], [, b]) => b - a);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-brand-primary">ダッシュボード</h2>
          <p className="text-xs text-gray-500 mt-1">
            最終更新: {new Date(data.as_of).toLocaleString("ja-JP")}
          </p>
        </div>
        <button className="btn-secondary" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? "更新中…" : "再読み込み"}
        </button>
      </div>

      {newCount > 0 && (
        <div className="bg-red-50 border border-red-300 text-red-800 p-4 rounded">
          <div className="flex items-start gap-3">
            <span className="text-xl leading-none">⚠️</span>
            <div className="flex-1">
              <p className="font-bold">
                未処理の NEW 受注が {newCount} 件あります
              </p>
              <p className="text-sm mt-1">
                受注取得直後で「これから一括ラベル発行する」状態なら正常です。
                <strong className="text-red-900">
                  「一括ラベル発行」を押した直後にもこの数字が残っている場合、ラベル発行が漏れている可能性があります。
                </strong>
                その場合は「受注取得」画面でもう一度「一括ラベル発行」を押してください。
              </p>
              {newByPlatform.length > 0 && (
                <ul className="text-sm mt-2 ml-4 list-disc">
                  {newByPlatform.map(([p, c]) => (
                    <li key={p}>
                      <span className="font-mono">{p}</span>: {c} 件
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <section>
        <h3 className="text-lg font-semibold mb-3">受注ステータス</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <StatCard label="全受注" value={total} />
          {Object.entries(STATUS_LABELS).map(([status, label]) => (
            <StatCard key={status} label={label} value={byStatus[status] ?? 0} />
          ))}
        </div>
      </section>

      <section>
        <h3 className="text-lg font-semibold mb-3">プラットフォーム別件数</h3>
        <div className="card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="border-b text-gray-500 text-left">
              <tr>
                <th className="py-2 pr-4">プラットフォーム</th>
                {Object.entries(STATUS_LABELS).map(([k, label]) => (
                  <th key={k} className="py-2 px-3 text-right">{label}</th>
                ))}
                <th className="py-2 pl-3 text-right font-medium">合計</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(byPlatform).length === 0 && (
                <tr>
                  <td colSpan={Object.keys(STATUS_LABELS).length + 2} className="py-6 text-center text-gray-400">
                    受注がまだありません
                  </td>
                </tr>
              )}
              {Object.entries(byPlatform).map(([p, counts]) => {
                const sum = Object.values(counts).reduce((a, b) => a + b, 0);
                return (
                  <tr key={p} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium">{p}</td>
                    {Object.keys(STATUS_LABELS).map((s) => (
                      <td key={s} className="py-2 px-3 text-right tabular-nums">
                        {counts[s] ?? 0}
                      </td>
                    ))}
                    <td className="py-2 pl-3 text-right font-bold tabular-nums">{sum}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <section>
          <h3 className="text-lg font-semibold mb-3">印刷ジョブ</h3>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(PRINT_STATUS_LABELS).map(([k, label]) => (
              <StatCard key={k} label={label} value={data.print_jobs[k] ?? 0} />
            ))}
          </div>
        </section>

        <section>
          <h3 className="text-lg font-semibold mb-3">本日の発送完了</h3>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(FULFILL_STATUS_LABELS).map(([k, label]) => (
              <StatCard key={k} label={label} value={data.fulfillment_today[k] ?? 0} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
